/** Геометрия стен и проёмов, фильтрация графа стен по этапам ремонта. */
import { angleOf, dist, sub, type Pt } from "@/shared/lib/geometry";
import type { Opening, Wall, WallGraph } from "./types-def";

/**
 * Оставить в графе только стены, подходящие под предикат, вместе с их проёмами и узлами.
 * Используется для вычисления актуальной геометрии плана на разных этапах:
 *   • Исходный план: `!wall.mounted` (все стены до демонтажа и монтажа)
 *   • Демонтаж / Монтаж / После монтажа (Мебель, Пол, 3D, Развёртки): `!wall.demolished`
 *     (`Исходный план − Демонтаж + Монтаж`).
 */
export const filterWallGraph = <T extends WallGraph>(
	plan: T,
	predicate: (wall: Wall) => boolean,
): T => {
	const walls: Record<string, Wall> = {};
	const usedNodes = new Set<string>();
	for (const wall of Object.values(plan.walls)) {
		if (!predicate(wall)) continue;
		walls[wall.id] = wall;
		usedNodes.add(wall.a);
		usedNodes.add(wall.b);
	}
	// Если все стены прошли фильтр — возвращаем исходный объект без лишних аллокаций
	if (Object.keys(walls).length === Object.keys(plan.walls).length) return plan;

	const openings: Record<string, Opening> = {};
	for (const opening of Object.values(plan.openings)) {
		if (walls[opening.wallId]) openings[opening.id] = opening;
	}
	const nodes: WallGraph["nodes"] = {};
	for (const id of usedNodes) {
		const node = plan.nodes[id];
		if (node) nodes[id] = node;
	}
	return { ...plan, nodes, walls, openings };
};

/** Стены исходного плана (без возводимых перегородок «Монтаж»). */
export const initialWallGraph = <T extends WallGraph>(plan: T): T =>
	filterWallGraph(plan, (wall) => !wall.mounted);

/**
 * Реальная картина стен после демонтажа и монтажа:
 * все стены из «Исходного плана» минус стены из «Демонтажа» плюс стены из «Монтажа».
 */
export const effectiveWallGraph = <T extends WallGraph>(plan: T): T =>
	filterWallGraph(plan, (wall) => !wall.demolished);

export const wallPoints = (plan: WallGraph, wall: Wall): { a: Pt; b: Pt } | null => {
	const a = plan.nodes[wall.a];
	const b = plan.nodes[wall.b];
	if (!a || !b) return null;
	return { a, b };
};

export const wallLength = (plan: WallGraph, wall: Wall): number => {
	const p = wallPoints(plan, wall);
	return p ? dist(p.a, p.b) : 0;
};

export const wallAngle = (plan: WallGraph, wall: Wall): number => {
	const p = wallPoints(plan, wall);
	return p ? angleOf(sub(p.b, p.a)) : 0;
};

export const openingsOfWall = (plan: WallGraph, wallId: string): Opening[] =>
	Object.values(plan.openings)
		.filter((o) => o.wallId === wallId)
		.toSorted((x, y) => x.offset - y.offset);

export interface WallPiece {
	x0: number;
	x1: number;
	z0: number;
	z1: number;
}

export const wallPieces = (wall: Wall, length: number, openings: Opening[]): WallPiece[] => {
	const pieces: WallPiece[] = [];
	const items = openings
		.map((o) => {
			const half = o.width / 2;
			return {
				x0: Math.max(0, o.offset - half),
				x1: Math.min(length, o.offset + half),
				z0: Math.max(0, o.sill),
				z1: Math.min(wall.height, o.sill + o.height),
			};
		})
		.filter((o) => o.x1 > o.x0)
		.toSorted((a, b) => a.x0 - b.x0);

	let cursor = 0;
	for (const o of items) {
		if (o.x0 > cursor) pieces.push({ x0: cursor, x1: o.x0, z0: 0, z1: wall.height });
		if (o.z0 > 0) pieces.push({ x0: o.x0, x1: o.x1, z0: 0, z1: o.z0 });
		if (o.z1 < wall.height) pieces.push({ x0: o.x0, x1: o.x1, z0: o.z1, z1: wall.height });
		cursor = Math.max(cursor, o.x1);
	}
	if (cursor < length) pieces.push({ x0: cursor, x1: length, z0: 0, z1: wall.height });

	return pieces.filter((p) => p.x1 - p.x0 > 1 && p.z1 - p.z0 > 1);
};

export const pointAlongWall = (a: Pt, b: Pt, offset: number): Pt => {
	const l = dist(a, b);
	if (l < 1e-6) return a;
	const t = offset / l;
	return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
};

export const clampOffset = (offset: number, width: number, length: number): number => {
	const half = width / 2;
	if (length <= width) return length / 2;
	return Math.max(half, Math.min(length - half, offset));
};
